import { ComparatorContext, useField } from '@kizi/comparator-core';
import {
  Button,
  Div,
  DivProps,
  Heading,
  Image,
  List,
  ListItem,
  P,
  ParagraphProps,
  Span,
} from '@kizi/core-components';
import { Countdown } from './Countdown';
import React, { useContext, useState } from 'react';
import { COMPARATOR_TYPE } from '../funnels';
import { Desktop, Mobile } from '../utils/breakpoint';
import { handleEnergyData } from '../utils/product';
import { ComparatorType } from '../utils/types';
import { MailMeThisOverview, MailMeThisOverviewProps } from './modals';
import { Field } from './Product';
import { Window } from './Window';
export interface ProductSummaryLogo {
  alt?: string;
  url: string;
}
interface RowItem extends Field {
  textContainerProps?: ParagraphProps;
}
interface GroupRowItem extends RowItem {
  details?: {
    title: string;
    rows: RowItem[];
    containerProps?: DivProps;
  };
}

export interface Group {
  title?: string | React.ReactNode;
  rows?: GroupRowItem[];
  disclaimer?: string;
  disclaimerColor?: string;
}

export interface ProductSummaryProps extends DivProps {
  logo: ProductSummaryLogo;
  title: string;
  groups: Group[];
  preview: string;
  saveForLater?: MailMeThisOverviewProps;
  disclaimerColor?: string;
  amountOfAvailableContracts?: number;
}

export interface ProductSummaryBodyProps
  extends Omit<ProductSummaryProps, 'preview'> {}

export const ProductSummaryPriceRow: React.FC<GroupRowItem> = ({
  label,
  value,
  details,
  textContainerProps,
}) => (
  <ListItem>
    <P
      display="flex"
      flexDirection="row"
      flex={1}
      justifyContent="space-between"
      variant="footnoteRegular"
      alignItems={'center'}
      css={{
        '& > div': {
          alignSelf: 'center',
        },
      }}
      {...textContainerProps}
    >
      <Span>{label}</Span>

      <Span ml={1} textAlign="right">
        {value}
      </Span>
    </P>

    {details && (
      <Div mt={5} {...details.containerProps}>
        <Span color="additionalInfo">{details.title}</Span>
        <List pl={4} mt={2}>
          {details.rows.map((item, detailIndex) => (
            <ProductSummaryPriceRow {...item} key={detailIndex} />
          ))}
        </List>
      </Div>
    )}
  </ListItem>
);

export const ProductSummaryGroup: React.FC<Group> = ({
  title,
  rows,
  disclaimer,
  disclaimerColor,
}) => {
  return (
    <ListItem>
      <Div variant="formGroup" flex={1}>
        {title && (
          <P mx={0} my={0} variant="footnoteImportant">
            {title}
          </P>
        )}

        {rows && rows.length > 0 && (
          <List>
            {rows?.map(
              ({ label, value, component: Component, ...props }, index) => {
                return (
                  <Div key={index}>
                    <ProductSummaryPriceRow
                      label={label}
                      value={value}
                      {...props}
                    />
                    {Component}
                  </Div>
                );
              }
            )}
          </List>
        )}
        {disclaimer && (
          <P
            variant="captionRegularSmallInfo"
            color={disclaimerColor}
            fontSize={1}
          >
            {disclaimer}
          </P>
        )}
      </Div>
    </ListItem>
  );
};

export const ProductSummaryHeader: React.FC<{
  title?: string;
  logo: ProductSummaryLogo;
  preview: string;
  amountOfAvailableContracts?: number;
  type: string;
}> = ({ title, logo, preview, amountOfAvailableContracts, type }) => (
  <Div variant="formGroup" width="100%">
    <Div
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      mb={3}
    >
      <Image alt={logo.alt} src={logo.url} maxHeight={26} width="fit-content" />

      {amountOfAvailableContracts && type !== 'energy-auction' && (
        <Countdown reverted amount={amountOfAvailableContracts} mr={-3} />
      )}
    </Div>
    {title && (
      <Heading as="h4" mx={0} my={0} variant="h4">
        {title}
      </Heading>
    )}
    <P variant="footnoteRegular">{preview}</P>
  </Div>
);

export const ProductSummaryBody: React.FC<ProductSummaryBodyProps> = ({
  logo,
  title,
  groups,
  saveForLater,
  amountOfAvailableContracts,
  disclaimerColor,
}: ProductSummaryBodyProps) => {
  const app = useContext(ComparatorContext);
  return (
    <List variant="primary">
      {/* header */}
      <ListItem>
        <Div variant="formGroup" width="100%">
          <Div
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Image alt={logo.alt} src={logo.url} maxHeight={26} mr={'auto'} />

            {amountOfAvailableContracts && app.name !== 'energy-auction' && (
              <Countdown reverted amount={2} mr={[-4, null, -6]} />
            )}
          </Div>
          {title && (
            <Heading as="h4" mx={0} my={0} variant="h4">
              {title}
            </Heading>
          )}
        </Div>
      </ListItem>

      {/* body */}
      {groups.map(({ title: groupTitle, rows, disclaimer }, index) => (
        <ProductSummaryGroup
          title={groupTitle}
          rows={rows}
          key={index}
          disclaimer={disclaimer}
          disclaimerColor={disclaimerColor}
        />
      ))}

      {/* footer */}
      {saveForLater && (
        <ListItem my={0}>
          <Div
            display="flex"
            flexDirection="row"
            justifyContent="center"
            flex={1}
            pb={[3, 0]}
          >
            <MailMeThisOverview {...saveForLater} />
          </Div>
        </ListItem>
      )}
    </List>
  );
};

export const ProductSummary: React.FC<ProductSummaryProps> = ({
  logo,
  title,
  groups,
  preview,
  amountOfAvailableContracts,
  saveForLater,
  disclaimerColor = 'second.1',
  ...divProps
}) => {
  const app = useContext(ComparatorContext);
  const [showMobileDetails, setShowMobileDetails] = useState<boolean>(false);
  return (
    <>
      <Desktop>
        <Div
          bg="background"
          maxWidth="300px"
          borderRadius={2}
          boxShadow="default"
          p={6}
          {...divProps}
        >
          <ProductSummaryBody
            logo={logo}
            title={title}
            groups={groups}
            saveForLater={saveForLater}
            amountOfAvailableContracts={amountOfAvailableContracts}
            disclaimerColor={disclaimerColor}
          />
        </Div>
      </Desktop>
      <Mobile>
        <Div
          bg="white"
          borderRadius={2}
          boxShadow="default"
          p={3}
          display="flex"
          flexDirection="row"
          flex={1}
          justifyContent="space-between"
          {...divProps}
        >
          <ProductSummaryHeader
            preview={preview}
            logo={logo}
            amountOfAvailableContracts={amountOfAvailableContracts}
            type={app.name}
          />
          <Button
            variant="transparent"
            alignSelf="end"
            onClick={() => {
              setShowMobileDetails(true);
            }}
          >
            Meer
          </Button>
        </Div>
        <Window
          onClose={() => {
            setShowMobileDetails(false);
          }}
          show={showMobileDetails}
          title="Details"
        >
          <ProductSummaryBody
            logo={logo}
            title={title}
            groups={groups}
            saveForLater={saveForLater}
            amountOfAvailableContracts={amountOfAvailableContracts}
            disclaimerColor={disclaimerColor}
          />
        </Window>
      </Mobile>
    </>
  );
};

export interface ProductSummaryWrapperProps extends DivProps {
  saveForLater?: MailMeThisOverviewProps;
  selectedProduct: string;
  type: ComparatorType;
  variant?: string;
  disclaimerColor?: string;
}

export const ProductSummaryWrapper: React.FC<ProductSummaryWrapperProps> = ({
  saveForLater,
  selectedProduct,
  type,
  variant,
  disclaimerColor = 'second.1',
  ...divProps
}) => {
  const field = useField(selectedProduct);
  if (!field || !field.value) return null;

  const data = JSON.parse(field.value as string);

  if (COMPARATOR_TYPE.ENERGY === type) {
    return (
      <ProductSummary
        {...handleEnergyData(data, variant, {
          fontSize: '12px',
          minWidth: '96px',
          width: '96px',
          mb: 1,
        })}
        saveForLater={saveForLater}
        disclaimerColor={disclaimerColor}
        {...divProps}
      />
    );
  }
  return null;
};

export default ProductSummaryWrapper;
